// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import xngUpdateCart from "@conklin/App/Handlers/Cart/UpdateCart";
import {
    canCheckoutWithCart,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@conklin/App/Store/Selectors/CartSelectors";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import StyledBox from "@conklin/XDL/Components/Common/Box";
import { parseBoolean } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentUserIsGuest,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import {
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import get from "@insite/mobius/utilities/get";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    includeActions = "includeActions",
    // XNG-Changes
    linesType = "linesType",
}

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getCurrentPromotionsDataView(state);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        accountSettings: { allowGuestCheckout },
        cartSettings: { onePageCheckout, showTaxAndShipping },
    } = getSettingsCollection(state);
    // XNG-Changes
    const isAuthenticated = xIsAuthenticated(state);
    return {
        isLoading: isCartLoading,
        cart,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showTaxAndShipping,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        // XNG-Changes
        canCheckoutWithCart: canCheckoutWithCart(cart, isAuthenticated),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            // XNG-Changes
            isCartCheckoutDisabled(cart, isAuthenticated) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        isCartEmpty: isCartEmpty(cart),
        // XNG-Changes
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart, isAuthenticated),
        // XNG-Changes
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart, isAuthenticated),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isPunchoutButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitRequisitionButtonDisabled: isCartLoading || isUpdatingCart,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        allowGuestCheckout,
        // XNG-Changes
        isAuthenticated: xIsAuthenticated(state),
        requestQuoteFormPageUrl: getPageLinkByPageType(state, "RequestQuoteFormPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
    signInAsGuest,
    // XNG-Changes
    xngUpdateCart,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeActions]: boolean;
        // XNG-Changes
        [fields.linesType]: "default" | "requiresQuote";
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    checkoutButton?: ButtonPresentationProps;
    cancelPunchOutButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    quoteRequiredModal?: ModalPresentationProps;
    submitRequisitionButton?: ButtonPresentationProps;
    // XNG-Changes
    fulfillmentMethods?: InjectableCss;
    domesticWrapper?: InjectableCss;
    messageText?: TypographyPresentationProps;
    radioGroupStyle?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioStyle?: FieldSetPresentationProps<RadioComponentProps>;
    quoteButton?: ButtonPresentationProps;
    internationalMessageText?: TypographyPresentationProps;
}

export const cartTotalStyles: CartTotalStyles = {
    checkoutButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            position: fixed;
                            left: 0;
                            bottom: 0;
                            width: 100%;
                            z-index: ${get(theme, "zIndex.stickyFooter")};
                        `,
                        css`
                            width: 100%;
                            margin-top: 10px;
                            position: inherit;
                            left: inherit;
                            bottom: inherit;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cancelPunchOutButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitQuoteButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitRequisitionButton: {
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
};

const styles = cartTotalStyles;

const CartTotal = ({
    showTaxAndShipping,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    preloadCheckoutShippingData,
    history,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    isCartEmpty,
    isLoading,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
    isPunchOutOrder,
    isPunchoutButtonDisabled,
    isSubmitForQuoteButtonDisabled,
    isSubmitRequisitionButtonDisabled,
    doPunchOutCheckout,
    cancelPunchOut,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    fields,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    allowGuestCheckout,
    // XNG-Changes
    id,
    xngUpdateCart,
    isAuthenticated,
    requestQuoteFormPageUrl,
    currentUserIsGuest,
    cartPageUrl,
    signInPageUrl,
}: Props) => {
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const checkoutHandler = () => {
        if (!cart) {
            return;
        }

        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        // if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
        //     setQuoteRequiredModalIsOpen(true);
        //     return;
        // }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        // XNG-Changes
        if (onePageCheckout && onePageCheckoutPageUrl && signInPageUrl) {
            // if (!isAuthenticated && !rememberMe) {
            //     signInAsGuest({ returnUrl: `${signInPageUrl}?returnUrl=/OnePageCheckout` });
            // } else {
            const onePageCheckoutUrl = isAwaitingApproval
                ? `${onePageCheckoutPageUrl}?cartId=${cartId}`
                : onePageCheckoutPageUrl;
            if (!isAuthenticated && !rememberMe && allowGuestCheckout) {
                history.push(`${signInPageUrl}?returnUrl=${onePageCheckoutUrl}`);
            } else {
                history.push(onePageCheckoutUrl);
            }
            // }
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const cancelPunchOutHandler = () => {
        cancelPunchOut();
    };

    const submitForQuoteClickHandler = () => {
        // Xng-Changes: adding check for Request Quote Form page
        if (!isAuthenticated && requestQuoteFormPageUrl) {
            history.push(requestQuoteFormPageUrl);
        } else if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };

    const submitAllProductsForQuoteClickHandler = () => {
        if (requestQuoteFormPageUrl) {
            history.push(`${requestQuoteFormPageUrl}?allproducts=true`);
        }
    };

    // const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Request Quote");
    // XNG-Change
    const hasQuoteProducts =
        (
            cart?.cartLines?.filter(
                l =>
                    l.quoteRequired ||
                    (!isAuthenticated && parseBoolean(l.properties.authRequiredForPurchase)) ||
                    parseBoolean(l.properties.isQuote),
            ) || []
        ).length > 0;

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    // XNG-Changes
    const handleInternationalOrderChange = (value: string) => {
        if (!isAuthenticated && !currentUserIsGuest) {
            signInAsGuest({
                returnUrl: cartPageUrl || "/cart",
                onComplete: () => {
                    xngUpdateCart({
                        cartToUpdate: {
                            properties: {
                                ...cart?.properties,
                                isInternationalOrder: value === "true",
                            } as any,
                        },
                    });
                },
            });
        } else {
            xngUpdateCart({
                cartToUpdate: {
                    properties: {
                        ...cart?.properties,
                        isInternationalOrder: value === "true",
                    } as any,
                },
            });
        }
    };

    // XNG-Changes
    // if (fields.linesType === "requiresQuote") {
    //     return (
    //         <StyledBox>
    //             <Typography variant="h3" style={{ marginBottom: 0 }}>
    //                 {translate("Requires Quote")}
    //             </Typography>
    //             <Divider margin="12px 0" printDisplay="none" />
    //             <Button
    //                 {...styles.submitQuoteButton}
    //                 disabled={isSubmitForQuoteButtonDisabled || !cart?.canRequestQuote}
    //                 onClick={submitForQuoteClickHandler}
    //                 data-test-selector="cartTotal_submitQuote"
    //             >
    //                 {submitForQuoteLabel}
    //             </Button>
    //         </StyledBox>
    //     );
    // }
    const locationBasedMsg = siteMessage("Custom_OrderSummary_LocationBasedPricing").toString();

    return (
        <StyledBox>
            <CartTotalDisplay
                showTaxAndShipping={showTaxAndShipping}
                extendedStyles={styles.cartTotal}
                isLoading={isLoading}
                isCartEmpty={isCartEmpty}
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
            />

            {/* XNG-Change */}
            <StyledWrapper {...styles.fulfillmentMethods}>
                <XngZone hint="Fulfillment Method" zoneName="FulfillmentMethod" contentId={id} />
            </StyledWrapper>

            <StyledWrapper {...styles.domesticWrapper}>
                <RadioGroup
                    label={translate("Domestic or International Order")}
                    value={cart?.properties.isInternationalOrder || "false"}
                    onChangeHandler={event => handleInternationalOrderChange(event.currentTarget.value)}
                    {...styles.radioGroupStyle}
                    data-test-selector="domesticOrInternationalOrder"
                    disabled={cart?.cartLines?.length === 0}
                >
                    <Radio
                        value="false"
                        {...styles.radioStyle}
                        data-test-selector="domesticOrInternationalOrder_Domestic"
                    >
                        {translate("Domestic")}
                    </Radio>
                    <Radio
                        value="true"
                        {...styles.radioStyle}
                        data-test-selector="domesticOrInternationalOrder_International"
                    >
                        {translate("International")}
                    </Radio>
                </RadioGroup>
                {/* XNG-Changes */}
                {parseBoolean(cart?.properties.isInternationalOrder) && (
                    <Typography {...styles.internationalMessageText}>
                        {siteMessage("Custom_OrderSummary_International")}
                    </Typography>
                )}
            </StyledWrapper>

            <Typography {...styles.messageText}>{siteMessage("Custom_OrderSummary_ShippingAndHandling")}</Typography>

            <Typography {...styles.messageText}>{siteMessage("Custom_OrderSummary_MicroCopy")}</Typography>
            {locationBasedMsg !== "Custom_OrderSummary_LocationBasedPricing" && (
                <Typography {...styles.messageText}>{locationBasedMsg}</Typography>
            )}

            {fields.includeActions !== false && canCheckoutWithCart && (
                <>
                    <Button
                        {...styles.checkoutButton}
                        onClick={checkoutHandler}
                        // XNG-Changes
                        disabled={
                            isCartCheckoutDisabled ||
                            hasOnlyQuoteRequiredProducts ||
                            parseBoolean(cart?.properties.isInternationalOrder)
                        }
                        data-test-selector="cartTotal_checkout"
                    >
                        {cart?.requiresApproval
                            ? translate("Checkout for Approval")
                            : translate("Continue to Checkout")}
                    </Button>
                    {isPunchOutOrder && (
                        <Button
                            {...styles.cancelPunchOutButton}
                            disabled={isPunchoutButtonDisabled}
                            onClick={cancelPunchOutHandler}
                        >
                            {translate("Cancel PunchOut")}
                        </Button>
                    )}
                    <TwoButtonModal
                        headlineText=""
                        modalIsOpen={quoteRequiredModalIsOpen}
                        messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Continue")}
                        onCancel={() => setQuoteRequiredModalIsOpen(false)}
                        onSubmit={checkoutHandler}
                        submitTestSelector="QuoteInCartMessageSubmit"
                        dataTestSelector="twoButtonModal_RequiresQuote"
                    ></TwoButtonModal>
                </>
            )}
            {/* XNG-Changes */}
            {/* {!isAuthenticated && !hasQuoteProducts && fields.includeActions !== false && canSubmitForQuote && (
                <Button
                    {...styles.quoteButton}
                    disabled={isSubmitForQuoteButtonDisabled}
                    onClick={submitAllProductsForQuoteClickHandler}
                    data-test-selector="cartTotal_submitQuote"
                >
                    {submitForQuoteLabel}
                </Button>
            )} */}
            {/* {fields.includeActions !== false && (
                <CartSaveOrderButton variant="button" extendedStyles={styles.saveOrderButton} />
            )} */}
            {fields.includeActions !== false && canSubmitRequisition && (
                <Button
                    {...styles.submitRequisitionButton}
                    disabled={isSubmitRequisitionButtonDisabled || parseBoolean(cart?.properties?.erpSystemIsDown)}
                    onClick={submitRequisitionClickHandler}
                    data-test-selector="cartTotal_submitRequisition"
                >
                    {translate("Submit Requisition")}
                </Button>
            )}
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartTotal)),
    definition: {
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.includeActions,
                displayName: "Include Actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 0,
            },
            // <XNG-Changes>
            {
                name: fields.linesType,
                displayName: "Lines Type",
                editorTemplate: "DropDownField",
                defaultValue: "default",
                options: [
                    { displayName: "Default", value: "default" },
                    // { displayName: "Requires Quote", value: "requiresQuote" },
                ],
                fieldType: "Contextual",
            },
            // </XNG-Changes>
        ],
    },
};

export default widgetModule;
